import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchUserCustomPricing,
  searchProductsForPricing,
  createPricingRule,
  updatePricingRule,
  deletePricingRule,
  clearSearchResults,
  selectUserData,
  selectPricingRules,
  selectSearchResults,
  selectIsLoading,
  selectIsSearching,
  selectIsCreating,
  selectIsUpdating,
  selectIsDeleting,
} from "../../RTK/users/userCustomPricingSlice";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Search,
  Edit3,
  Trash2,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Package,
  DollarSign,
  User,
  Save,
  X,
  Info,
  Calendar,
  Clock,
  Tag,
  Activity,
  CheckCircle,
  XCircle,
  Power,
  PowerOff,
  Percent,
  Minus,
} from "lucide-react";

// ✅ Delete Confirmation Popup
const DeleteConfirmPopup = ({ rule, onConfirm, onCancel, isDeleting }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-gray-900 border border-red-500/30 rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-red-500/20 rounded-full">
            <AlertCircle className="w-6 h-6 text-red-400" />
          </div>
          <h3 className="text-xl font-bold text-white">تأكيد الحذف</h3>
        </div>

        <div className="space-y-4 mb-6">
          <p className="text-gray-300">
            هل أنت متأكد من حذف قاعدة التسعير التالية؟
          </p>

          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              {(rule.product?.image || rule.product_image) && (
                <img
                  src={rule.product?.image || rule.product_image}
                  alt={rule.product?.name || rule.product_name || "Product"}
                  className={`w-12 h-12 rounded-lg object-cover flex-shrink-0`}
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/48?text=No+Image";
                  }}
                />
              )}
              <div className="flex-1">
                <p className="text-white font-medium text-sm">
                  {rule.product?.name || rule.product_name || "منتج محذوف"}
                </p>
                <p className="text-gray-400 text-xs">
                  ID: {rule.product?.id || rule.product_id || "N/A"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm">
              <div
                className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                  rule.adjustment_type === "decrease"
                    ? "bg-green-500/10 text-green-400"
                    : rule.adjustment_type === "increase"
                      ? "bg-red-500/10 text-red-400"
                      : rule.adjustment_type === "at_cost"
                        ? "bg-blue-500/10 text-blue-400"
                        : "bg-purple-500/10 text-purple-400"
                }`}
              >
                {rule.adjustment_type === "decrease" ? (
                  <TrendingDown className="w-3 h-3" />
                ) : rule.adjustment_type === "increase" ? (
                  <TrendingUp className="w-3 h-3" />
                ) : rule.adjustment_type === "at_cost" ? (
                  <Minus className="w-3 h-3" />
                ) : (
                  <Percent className="w-3 h-3" />
                )}
                <span className="font-medium">{rule.percentage}%</span>
              </div>

              {rule.pricing_summary && (
                <div className="text-xs text-gray-400">
                  السعر: ${rule.pricing_summary.adjusted_price}
                </div>
              )}
            </div>

            {rule.notes && (
              <p className="text-gray-500 text-xs border-t border-gray-700 pt-2">
                <Info className="inline w-3 h-3 mr-1" />
                {rule.notes}
              </p>
            )}
          </div>

          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
            <p className="text-red-300 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              سيتم حذف هذه القاعدة نهائياً ولن يمكن استرجاعها
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            إلغاء
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                جاري الحذف...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                حذف نهائياً
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

function UserCustomPricing() {
  const { profile_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const userData = useSelector(selectUserData);
  const rules = useSelector(selectPricingRules);
  const searchResults = useSelector(selectSearchResults);
  const isLoading = useSelector(selectIsLoading);
  const isSearching = useSelector(selectIsSearching);
  const isCreating = useSelector(selectIsCreating);
  const isUpdating = useSelector(selectIsUpdating);
  const isDeleting = useSelector(selectIsDeleting);

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteConfirmRule, setDeleteConfirmRule] = useState(null);
  const [formData, setFormData] = useState({
    product_id: null,
    adjustment_type: "decrease",
    percentage: "",
    notes: "",
  });

  // Fetch pricing rules on mount
  useEffect(() => {
    dispatch(fetchUserCustomPricing(profile_id));
  }, [dispatch, profile_id]);

  // ✅ Debounced search - only when modal is open
  useEffect(() => {
    if (showAddModal && searchQuery.trim().length > 2) {
      const timer = setTimeout(() => {
        dispatch(searchProductsForPricing(searchQuery));
      }, 500);
      return () => clearTimeout(timer);
    } else {
      dispatch(clearSearchResults());
    }
  }, [searchQuery, showAddModal, dispatch]);

  // ✅ Auto-set percentage to 0 for at_cost and level_only
  useEffect(() => {
    if (
      formData.adjustment_type === "at_cost" ||
      formData.adjustment_type === "level_only"
    ) {
      setFormData((prev) => ({ ...prev, percentage: "0" }));
    }
  }, [formData.adjustment_type]);

  // Create pricing rule
  const handleCreateRule = async () => {
    if (!formData.product_id) {
      toast.error("يرجى تحديد المنتج");
      return;
    }

    // ✅ Validate percentage based on type
    if (
      formData.adjustment_type === "at_cost" ||
      formData.adjustment_type === "level_only"
    ) {
      // Auto set to 0
      formData.percentage = "0";
    } else {
      const percentage = parseFloat(formData.percentage);
      if (!formData.percentage || percentage <= 0 || percentage > 100) {
        toast.error("النسبة يجب أن تكون بين 0 و 100");
        return;
      }
    }

    try {
      await dispatch(
        createPricingRule({
          profile_id,
          ruleData: {
            product_id: formData.product_id,
            adjustment_type: formData.adjustment_type,
            percentage: parseFloat(formData.percentage),
            notes: formData.notes || "",
          },
        }),
      ).unwrap();

      // ✅ Fetch all rules again to get complete data
      await dispatch(fetchUserCustomPricing(profile_id)).unwrap();

      toast.success(" تم إضافة قاعدة التسعير بنجاح");
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      toast.error(error);
    }
  };

  // Update pricing rule
  const handleUpdateRule = async (ruleId) => {
    // ✅ Validate percentage based on type
    if (
      formData.adjustment_type === "at_cost" ||
      formData.adjustment_type === "level_only"
    ) {
      formData.percentage = "0";
    } else {
      const percentage = parseFloat(formData.percentage);
      if (!percentage || percentage <= 0 || percentage > 100) {
        toast.error("النسبة يجب أن تكون بين 0 و 100");
        return;
      }
    }

    try {
      await dispatch(
        updatePricingRule({
          profile_id,
          rule_id: ruleId,
          ruleData: {
            adjustment_type: formData.adjustment_type,
            percentage: parseFloat(formData.percentage),
            is_active: true,
            notes: formData.notes || "",
          },
        }),
      ).unwrap();

      // ✅ Fetch all rules again to get complete data
      await dispatch(fetchUserCustomPricing(profile_id)).unwrap();

      toast.success(" تم تحديث القاعدة بنجاح");
      setEditingRule(null);
    } catch (error) {
      toast.error(error);
    }
  };

  // ✅ Toggle is_active
  const handleToggleActive = async (rule) => {
    try {
      await dispatch(
        updatePricingRule({
          profile_id,
          rule_id: rule.id,
          ruleData: {
            adjustment_type: rule.adjustment_type,
            percentage: parseFloat(rule.percentage),
            is_active: !rule.is_active,
            notes: rule.notes || "",
          },
        }),
      ).unwrap();

      // ✅ Fetch all rules again to get complete data
      await dispatch(fetchUserCustomPricing(profile_id)).unwrap();

      toast.success(
        rule.is_active ? "⚠️ تم تعطيل القاعدة" : "✅ تم تفعيل القاعدة",
      );
    } catch (error) {
      toast.error(error);
    }
  };

  // Delete pricing rule
  const handleDeleteClick = (rule) => {
    setDeleteConfirmRule(rule);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmRule) return;

    try {
      await dispatch(
        deletePricingRule({
          profile_id,
          rule_id: deleteConfirmRule.id,
        }),
      ).unwrap();

      // ✅ No need to fetch again after delete, just remove from state
      toast.success("✅ تم حذف القاعدة بنجاح");
      setDeleteConfirmRule(null);
    } catch (error) {
      toast.error(error);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      product_id: null,
      adjustment_type: "decrease",
      percentage: "",
      notes: "",
    });
    setSearchQuery("");
    setSelectedProduct(null);
    dispatch(clearSearchResults());
  };

  // ✅ Format date helper with error handling
  const formatDate = (dateString) => {
    if (!dateString) return "غير متوفر";

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return "تاريخ غير صالح";
      }

      return new Intl.DateTimeFormat("ar-EG", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return "غير متوفر";
    }
  };

  // ✅ Calculate time ago with error handling
  const getTimeAgo = (dateString) => {
    if (!dateString) return "غير متوفر";

    try {
      const now = new Date();
      const past = new Date(dateString);

      if (isNaN(past.getTime())) {
        return "غير متوفر";
      }

      const diffMs = now - past;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "الآن";
      if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
      if (diffHours < 24) return `منذ ${diffHours} ساعة`;
      return `منذ ${diffDays} يوم`;
    } catch (error) {
      console.error("Error calculating time ago:", dateString, error);
      return "غير متوفر";
    }
  };

  // ✅ Get adjustment type label and style
  const getAdjustmentTypeInfo = (type) => {
    switch (type) {
      case "decrease":
        return {
          label: "تخفيض",
          icon: TrendingDown,
          bgColor: "from-green-500/20 to-emerald-500/20",
          textColor: "text-green-400",
          borderColor: "border-green-500/30",
        };
      case "increase":
        return {
          label: "زيادة",
          icon: TrendingUp,
          bgColor: "from-red-500/20 to-rose-500/20",
          textColor: "text-red-400",
          borderColor: "border-red-500/30",
        };
      case "at_cost":
        return {
          label: "بسعر التكلفة",
          icon: Minus,
          bgColor: "from-blue-500/20 to-cyan-500/20",
          textColor: "text-blue-400",
          borderColor: "border-blue-500/30",
        };
      case "level_only":
        return {
          label: "حسب المستوى فقط",
          icon: Percent,
          bgColor: "from-purple-500/20 to-pink-500/20",
          textColor: "text-purple-400",
          borderColor: "border-purple-500/30",
        };
      default:
        return {
          label: "غير محدد",
          icon: AlertCircle,
          bgColor: "from-gray-500/20 to-gray-600/20",
          textColor: "text-gray-400",
          borderColor: "border-gray-500/30",
        };
    }
  };

  // ✅ Calculate enhanced stats
  const stats = useMemo(() => {
    const decreaseRules = rules.filter((r) => r.adjustment_type === "decrease");
    const increaseRules = rules.filter((r) => r.adjustment_type === "increase");
    const atCostRules = rules.filter((r) => r.adjustment_type === "at_cost");
    const levelOnlyRules = rules.filter(
      (r) => r.adjustment_type === "level_only",
    );
    const activeRules = rules.filter((r) => r.is_active);

    const avgDecreasePercent =
      decreaseRules.length > 0
        ? (
            decreaseRules.reduce(
              (sum, r) => sum + parseFloat(r.percentage),
              0,
            ) / decreaseRules.length
          ).toFixed(2)
        : 0;

    const avgIncreasePercent =
      increaseRules.length > 0
        ? (
            increaseRules.reduce(
              (sum, r) => sum + parseFloat(r.percentage),
              0,
            ) / increaseRules.length
          ).toFixed(2)
        : 0;

    return [
      {
        title: "إجمالي القواعد",
        count: rules.length,
        icon: Package,
        color: "bg-blue-500/10",
        iconColor: "text-blue-500",
        borderColor: "border-blue-500/30",
        subtitle: `${activeRules.length} نشطة`,
      },
      {
        title: "قواعد التخفيض",
        count: decreaseRules.length,
        icon: TrendingDown,
        color: "bg-green-500/10",
        iconColor: "text-green-500",
        borderColor: "border-green-500/30",
        subtitle: `متوسط ${avgDecreasePercent}%`,
      },
      {
        title: "قواعد الزيادة",
        count: increaseRules.length,
        icon: TrendingUp,
        color: "bg-red-500/10",
        iconColor: "text-red-500",
        borderColor: "border-red-500/30",
        subtitle: `متوسط ${avgIncreasePercent}%`,
      },
    ];
  }, [rules]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-950">
        <Loader2 className="w-12 h-12 text-yellow-500 animate-spin mb-4" />
        <p className="text-gray-400">جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-3 sm:p-6" dir="rtl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-3 sm:gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors group flex-shrink-0 mt-1"
              title="رجوع"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-white transition-colors" />
            </button>
            <div className="flex-1">
              <h1 className="text-lg sm:text-2xl font-bold text-white flex items-center gap-2">
                <Tag className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500" />
                قواعد التسعير المخصصة
              </h1>
              {userData && (
                <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-3">
                  <div className="flex items-center gap-1.5 text-xs sm:text-sm">
                    <User className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                    <span className="font-medium text-white">
                      {userData.username}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <Activity className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                    <span className="text-yellow-400 text-xs sm:text-sm font-medium">
                      {userData.level}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                    <span className="text-green-400 text-xs sm:text-sm font-medium">
                      {userData.level_profit_pct}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => setShowAddModal(true)}
            disabled={isCreating}
            className="w-full lg:w-auto flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-black rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            إضافة قاعدة جديدة
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
        {stats.map((stat) => (
          <div
            key={stat.title}
            className={`bg-gray-900 border ${stat.borderColor} rounded-xl p-4 sm:p-5 hover:border-opacity-50 transition-all group`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-400 text-xs sm:text-sm mb-1">
                  {stat.title}
                </p>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2 group-hover:scale-110 transition-transform">
                  {stat.count}
                </h3>
                <p className={`text-xs ${stat.iconColor} font-medium`}>
                  {stat.subtitle}
                </p>
              </div>
              <div
                className={`p-3 sm:p-4 ${stat.color} rounded-xl group-hover:scale-110 transition-transform`}
              >
                <stat.icon
                  className={`w-6 h-6 sm:w-8 sm:h-8 ${stat.iconColor}`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Rules List */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
            <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500" />
            القواعد الحالية ({rules.length})
          </h2>
        </div>

        {rules.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="p-4 bg-gray-800/50 rounded-full w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 flex items-center justify-center">
              <Package className="w-8 h-8 sm:w-10 sm:h-10 text-gray-600" />
            </div>
            <p className="text-gray-400 text-base sm:text-lg font-medium mb-2">
              لا توجد قواعد تسعير مخصصة
            </p>
            <p className="text-gray-500 text-sm mb-6">
              اضغط على "إضافة قاعدة جديدة" لإنشاء قاعدة تسعير
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {rules.map((rule) => {
              const typeInfo = getAdjustmentTypeInfo(rule.adjustment_type);
              const isActive = rule.is_active;

              return (
                <div
                  key={rule.id}
                  className={`bg-gray-800 rounded-xl p-4 sm:p-5 hover:shadow-lg group transition-all ${
                    isActive
                      ? "border-2 border-green-500/50"
                      : "border-2 border-red-500/30"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* ✅ Mobile: Full width image, Desktop: Side by side */}
                    <div className="flex flex-col lg:flex-row lg:items-start gap-3 sm:gap-4 flex-1 min-w-0">
                      {/* ✅ Product Image - Full width on mobile */}
                      {(rule.product?.image || rule.product_image) && (
                        <div className="relative w-full lg:w-auto lg:flex-shrink-0">
                          <img
                            src={rule.product?.image || rule.product_image}
                            alt={
                              rule.product?.name ||
                              rule.product_name ||
                              "Product"
                            }
                            className={`w-full lg:w-20 h-48 sm:h-56 lg:h-20 rounded-xl object-cover border-2 transition-all ${
                              isActive
                                ? "border-green-500 group-hover:border-green-400"
                                : "border-red-500/50 group-hover:border-red-400/50 opacity-60"
                            }`}
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/400x200?text=No+Image";
                            }}
                          />
                          {(rule.product?.status || rule.product_status) && (
                            <div className="absolute top-2 right-2 lg:-top-2 lg:-right-2 px-2 py-1 bg-green-500 text-white text-xs rounded-full shadow-lg">
                              {rule.product?.status || rule.product_status}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ✅ Product Details - Full width below image on mobile */}
                      <div className="flex-1 space-y-3 min-w-0">
                        {/* Product Name + ID */}
                        <div>
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="text-white font-semibold text-base sm:text-lg truncate">
                              {rule.product?.name ||
                                rule.product_name ||
                                "منتج محذوف"}
                            </h3>
                            <span className="text-xs text-gray-500 bg-gray-700 px-2 py-1 rounded-md flex-shrink-0">
                              ID: {rule.product?.id || rule.product_id || "N/A"}
                            </span>
                          </div>
                          {(rule.product?.commission_rate ||
                            rule.commission_rate) && (
                            <p className="text-gray-400 text-xs flex items-center gap-1">
                              <Tag className="w-3 h-3" />
                              عمولة المنتج:{" "}
                              {rule.product?.commission_rate ||
                                rule.commission_rate}
                              %
                            </p>
                          )}
                        </div>

                        {/* Pricing Summary */}
                        {rule.pricing_summary && (
                          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3 space-y-3">
                            {/* Three Price Boxes */}
                            <div className="grid grid-cols-3 gap-2">
                              {/* Base Price */}
                              <div className="flex flex-col items-center justify-center p-2 bg-gray-800/50 rounded-lg border border-gray-700">
                                <span className="text-gray-400 text-[9px] sm:text-[10px] mb-1 text-center">
                                  السعر الأساسي

                                </span>
                                <span className="text-gray-300 text-xs sm:text-sm font-mono font-bold">
                                  $
                                  {rule.product?.base_price ||
                                    rule.base_price ||
                                    rule.pricing_summary?.base_price ||
                                    "0.00"}
                                </span>
                              </div>

                              {/* Normal Price */}
                              <div className="flex flex-col items-center justify-center p-2 bg-blue-500/10 rounded-lg border border-blue-500/30">
                                <span className="text-blue-400 text-[9px] sm:text-[10px] mb-1 text-center">
                                  السعر العادي

                                </span>
                                <span className="text-blue-400 text-xs sm:text-sm font-mono font-bold">
                                  ${rule.pricing_summary.normal_price}
                                </span>
                              </div>

                              {/* Adjusted Price */}
                              <div className="flex flex-col items-center justify-center p-2 bg-green-500/10 rounded-lg border border-green-500/30">
                                <span className="text-green-400 text-[9px] sm:text-[10px] mb-1 text-center">
                                  السعر المعدل

                                </span>
                                <span className="text-green-400 text-xs sm:text-sm font-mono font-bold">
                                  ${rule.pricing_summary.adjusted_price}
                                </span>
                              </div>
                            </div>

                            {/* Margin Info */}
                            <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-700">
                              <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-500/10 text-blue-400 rounded-md flex-1">
                                <Activity className="w-3 h-3 flex-shrink-0" />
                                <div className="flex flex-col min-w-0">
                                  <span className="text-[8px] sm:text-[9px] opacity-75">
                                    الإجمالي
                                  </span>
                                  <span className="text-[10px] sm:text-xs font-bold truncate">
                                    {rule.pricing_summary.total_margin_pct}%
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center gap-1.5 px-2 py-1 bg-green-500/10 text-green-400 rounded-md flex-1">
                                <CheckCircle className="w-3 h-3 flex-shrink-0" />
                                <div className="flex flex-col min-w-0">
                                  <span className="text-[8px] sm:text-[9px] opacity-75">
                                     سبة الربح النهائية
                                  </span>
                                  <span className="text-[10px] sm:text-xs font-bold truncate">
                                    {rule.pricing_summary.effective_margin_pct}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Adjustment Type + Status */}
                        <div className="flex flex-wrap items-center gap-2">
                          <div
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg shadow-md bg-gradient-to-r ${typeInfo.bgColor} ${typeInfo.textColor} border ${typeInfo.borderColor}`}
                          >
                            <typeInfo.icon className="w-4 h-4 flex-shrink-0" />
                            <span className="font-bold text-xs">
                              {typeInfo.label}
                              {(rule.adjustment_type === "decrease" ||
                                rule.adjustment_type === "increase") &&
                                ` ${rule.percentage}%`}
                            </span>
                          </div>

                          {/* Status Badge */}
                          <div
                            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] border ${
                              isActive
                                ? "bg-green-500/10 text-green-400 border-green-500/30"
                                : "bg-red-500/10 text-red-400 border-red-500/30"
                            }`}
                          >
                            {isActive ? (
                              <>
                                <CheckCircle className="w-2.5 h-2.5" />
                                نشطة
                              </>
                            ) : (
                              <>
                                <XCircle className="w-2.5 h-2.5" />
                                معطلة
                              </>
                            )}
                          </div>
                        </div>

                        {/* Notes */}
                        {rule.notes && (
                          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-2 sm:p-3">
                            <p className="text-blue-300 text-xs flex items-start gap-2">
                              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span className="break-words">{rule.notes}</span>
                            </p>
                          </div>
                        )}

                        {/* Metadata */}
                        {rule.created_at && (
                          <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500 border-t border-gray-700 pt-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-2.5 h-2.5 flex-shrink-0" />
                              <span className="truncate">
                                {formatDate(rule.created_at)}
                              </span>
                            </span>
                            {rule.updated_at &&
                              rule.updated_at !== rule.created_at && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-2.5 h-2.5 flex-shrink-0" />
                                  {getTimeAgo(rule.updated_at)}
                                </span>
                              )}
                            {rule.created_by && (
                              <span className="flex items-center gap-1">
                                <User className="w-2.5 h-2.5 flex-shrink-0" />
                                {rule.created_by}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ✅ Action Buttons - Bottom on mobile, Side on desktop */}
                    <div className="flex lg:flex-col gap-2 justify-end lg:justify-start">
                      {/* Toggle Active */}
                      <button
                        onClick={() => handleToggleActive(rule)}
                        disabled={isUpdating || isDeleting}
                        className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border ${
                          isActive
                            ? "bg-green-500/10 text-green-400 border-green-500/30 hover:bg-green-500/20"
                            : "bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20"
                        }`}
                        title={isActive ? "تعطيل" : "تفعيل"}
                      >
                        {isActive ? (
                          <Power className="w-4 h-4 sm:w-5 sm:h-5" />
                        ) : (
                          <PowerOff className="w-4 h-4 sm:w-5 sm:h-5" />
                        )}
                      </button>

                      {/* Edit */}
                      <button
                        onClick={() => {
                          setEditingRule(rule);
                          setFormData({
                            product_id: rule.product?.id || null,
                            adjustment_type: rule.adjustment_type,
                            percentage: rule.percentage,
                            notes: rule.notes || "",
                          });
                        }}
                        disabled={isUpdating || isDeleting}
                        className="p-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-blue-500/30"
                        title=" تعديل"
                      >
                        <Edit3 className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => handleDeleteClick(rule)}
                        disabled={isUpdating || isDeleting}
                        className="p-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-red-500/30"
                        title="حذف"
                      >
                        <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Edit Form */}
                  {editingRule?.id === rule.id && (
                    <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-700 animate-fadeIn">
                      <h4 className="text-white font-medium mb-4 flex items-center gap-2 text-sm sm:text-base">
                        <Edit3 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                        تعديل القاعدة
                      </h4>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                        <div>
                          <label className="block text-gray-400 text-sm mb-2 font-medium">
                            نوع التعديل *
                          </label>
                          <select
                            value={formData.adjustment_type}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                adjustment_type: e.target.value,
                              })
                            }
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white text-sm sm:text-base focus:outline-none focus:border-yellow-500 transition-colors appearance-none cursor-pointer"
                          >
                            <option value="decrease"> تخفيض</option>
                            <option value="increase"> زيادة</option>
                            <option value="at_cost"> بسعر التكلفة</option>
                            <option value="level_only">
                              حسب المستوى فقط (بدون عمولة المنتج)
                            </option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-gray-400 text-sm mb-2 font-medium">
                            النسبة المئوية (%) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={formData.percentage}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                percentage: e.target.value,
                              })
                            }
                            disabled={
                              formData.adjustment_type === "at_cost" ||
                              formData.adjustment_type === "level_only"
                            }
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white text-sm sm:text-base focus:outline-none focus:border-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder={
                              formData.adjustment_type === "at_cost" ||
                              formData.adjustment_type === "level_only"
                                ? "تلقائي: 0"
                                : "مثال: 5.50"
                            }
                          />
                        </div>

                        <div>
                          <label className="block text-gray-400 text-sm mb-2 font-medium">
                            ملاحظات (اختياري)
                          </label>
                          <input
                            type="text"
                            value={formData.notes}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                notes: e.target.value,
                              })
                            }
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white text-sm sm:text-base focus:outline-none focus:border-yellow-500 transition-colors"
                            placeholder="أضف ملاحظة..."
                          />
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mt-4 sm:mt-6">
                        <button
                          onClick={() => handleUpdateRule(rule.id)}
                          disabled={isUpdating}
                          className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg font-medium text-sm sm:text-base"
                        >
                          {isUpdating ? (
                            <>
                              <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                              جاري الحفظ...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 sm:w-5 sm:h-5" />
                              حفظ التعديلات
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => setEditingRule(null)}
                          disabled={isUpdating}
                          className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 text-sm sm:text-base"
                        >
                          <X className="w-4 h-4 sm:w-5 sm:h-5" />
                          إلغاء
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ✅ Add Modal with Search Inside */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-6 z-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Plus className="w-6 h-6 text-yellow-500" />
                  إضافة قاعدة تسعير جديدة
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Search Section */}
              <div>
                <label className="block text-gray-400 text-sm mb-2 font-medium">
                  البحث عن منتج *
                </label>
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ابحث بالاسم أو رقم المنتج..."
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg pr-10 pl-10 py-3 text-white focus:outline-none focus:border-yellow-500 transition-colors"
                  />
                  {isSearching && (
                    <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-500 animate-spin" />
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 pt-0">
              {/* Selected Product */}
              {selectedProduct && (
                <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={selectedProduct.image}
                      alt={selectedProduct.name}
                      className="w-16 h-16 rounded-lg object-cover border-2 border-green-500/50"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/64?text=No+Image";
                      }}
                    />
                    <div className="flex-1">
                      <p className="text-white font-semibold">
                        {selectedProduct.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                        <span className="bg-gray-700 px-2 py-0.5 rounded">
                          ID: {selectedProduct.id}
                        </span>
                        <span>${selectedProduct.base_price}</span>
                        <span className="text-green-400">
                          • {selectedProduct.status}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedProduct(null);
                        setFormData({ ...formData, product_id: null });
                        setSearchQuery("");
                      }}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>
                  <p className="text-green-400 text-sm flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    المنتج محدد - يمكنك الآن إضافة القاعدة
                  </p>
                </div>
              )}

              {/* Search Results */}
              {!selectedProduct && searchResults.length > 0 && (
                <div className="mb-6">
                  <p className="text-gray-400 text-sm mb-2">نتائج البحث:</p>
                  <div className="bg-gray-800 border border-gray-700 rounded-lg max-h-64 overflow-y-auto">
                    {searchResults.map((product) => (
                      <div
                        key={product.id}
                        onClick={() => {
                          setSelectedProduct(product);
                          setFormData({ ...formData, product_id: product.id });
                          setSearchQuery(product.name);
                          dispatch(clearSearchResults());
                        }}
                        className="flex items-center gap-3 p-3 hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-700 last:border-0"
                      >
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-12 h-12 rounded object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/48?text=No+Image";
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">
                            {product.name}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                            <span className="bg-gray-700 px-1.5 py-0.5 rounded">
                              ID: {product.id}
                            </span>
                            <span>${product.base_price}</span>
                            <span>• {product.status}</span>
                          </div>
                        </div>
                        <CheckCircle className="w-5 h-5 text-gray-600" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!selectedProduct &&
                searchQuery.length > 2 &&
                searchResults.length === 0 &&
                !isSearching && (
                  <div className="text-center py-8 mb-6">
                    <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500">
                      لا توجد نتائج للبحث "{searchQuery}"
                    </p>
                  </div>
                )}

              {/* Form - Only show when product is selected */}
              {selectedProduct && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-gray-400 text-sm mb-2 font-medium">
                        نوع التعديل *
                      </label>
                      <select
                        value={formData.adjustment_type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            adjustment_type: e.target.value,
                          })
                        }
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-3 text-white focus:outline-none focus:border-yellow-500 transition-colors"
                      >
                        <option value="decrease"> تخفيض</option>
                        <option value="increase"> زيادة</option>
                        <option value="at_cost"> بسعر التكلفة</option>
                        <option value="level_only">
                          حسب المستوى فقط (بدون عمولة المنتج)
                        </option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-gray-400 text-sm mb-2 font-medium">
                        النسبة المئوية (%) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.percentage}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            percentage: e.target.value,
                          })
                        }
                        disabled={
                          formData.adjustment_type === "at_cost" ||
                          formData.adjustment_type === "level_only"
                        }
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-3 text-white focus:outline-none focus:border-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder={
                          formData.adjustment_type === "at_cost" ||
                          formData.adjustment_type === "level_only"
                            ? "تلقائي: 0"
                            : "مثال: 5.50"
                        }
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-gray-400 text-sm mb-2 font-medium">
                      ملاحظات (اختياري)
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-3 text-white h-24 focus:outline-none focus:border-yellow-500 transition-colors resize-none"
                      placeholder="أضف أي ملاحظات هنا..."
                    />
                  </div>
                </>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  disabled={isCreating}
                  className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 font-medium"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleCreateRule}
                  disabled={
                    isCreating ||
                    !formData.product_id ||
                    (formData.adjustment_type !== "at_cost" &&
                      formData.adjustment_type !== "level_only" &&
                      !formData.percentage)
                  }
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      جاري الإضافة...
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      إضافة القاعدة
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {deleteConfirmRule && (
        <DeleteConfirmPopup
          rule={deleteConfirmRule}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteConfirmRule(null)}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
}

export default UserCustomPricing;
